"use client";

import { Popover, Tab, Transition } from "@headlessui/react";
import {
  BanknotesIcon,
  GlobeAltIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { FC, Fragment, useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "@/app/lib/hooks";
import { getLanguages, setSelectedLanguage } from "@/app/lib/reducers/languageReducer";
import { getCurrencies, setSelectedCurrency } from "@/app/lib/reducers/currenciesReducer";
import { RootState } from "@/app/lib/store";
import i18n from "@/app/i18n/i18n";
import { initI18n } from "@/app/i18n/i18n";
import useClickOutside from "@/hooks/useClickOutside";
import { useRouter, usePathname } from "next/navigation";
import { Route } from "next";
import { useTranslation } from "react-i18next";

const DEFAULT_CURRENCY = "EUR";

interface LanguageItem {
  name: string;
  nativeName: string;
  code: string;
  href: string;
  activated?: boolean;
}

interface CurrencyItem {
  code: string;
  name: string;
  symbol: string;
  activated?: boolean;
}

interface LangDropdownProps {
  panelClassName?: string;
  className?: string;
}

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const LangDropdown: FC<LangDropdownProps> = ({
  panelClassName = "top-full right-0 max-w-sm w-96",
  className = "hidden md:flex",
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("guydeez");
  const languages: LanguageItem[] = useAppSelector((state: RootState) => state.language.languages);
  const selectedLanguage = useAppSelector((state: RootState) => state.language.selectedLanguage);
  const currencies: CurrencyItem[] = useAppSelector((state: RootState) => state.currencies.currencies);
  const selectedCurrency = useAppSelector((state: RootState) => state.currencies.selectedCurrency);
  // Compte B2B a devise imposee (users.defaultCurrency) : seule celle-ci est proposee.
  // CurrencyLock (layout racine) force deja l'URL et le store ; ici on ne propose rien d'autre.
  const lockedCurrency: string = useAppSelector((state: RootState) => {
    const c = state.auth.user?.defaultCurrency;
    return typeof c === "string" && /^[A-Z]{3}$/.test(c) ? c : "";
  });

  const pathSegments = pathname.split("/").filter(Boolean);
  const urlLang: string = pathSegments[0] || "de_DE";
  const urlCurrency: string = pathSegments[1] || DEFAULT_CURRENCY;

  useEffect(() => {
    dispatch(getLanguages());
    dispatch(getCurrencies());
    initI18n(selectedLanguage || urlLang);
  }, [dispatch]);

  useEffect(() => {
    if (languages.length === 0 || selectedLanguage === urlLang) return;

    if (languages.some(lang => lang.code === urlLang)) {
      dispatch(setSelectedLanguage(urlLang));
      localStorage.setItem("lang", urlLang);
      document.cookie = `lang=${urlLang}; path=/; max-age=31536000; SameSite=Lax`;
      i18n.changeLanguage(urlLang);
    }
  }, [languages]);

  useEffect(() => {
    if (currencies.length === 0 || selectedCurrency === urlCurrency) return;

    if (currencies.some(cur => cur.code === urlCurrency)) {
      dispatch(setSelectedCurrency(urlCurrency));
      localStorage.setItem("currency", urlCurrency);
      document.cookie = `currency=${urlCurrency}; path=/; max-age=31536000; SameSite=Lax`;
    }
  }, [currencies]);

  useEffect(() => {
    if (i18n?.changeLanguage && selectedLanguage && i18n.language !== selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [selectedLanguage]);


  useClickOutside(dropdownRef, () => {
    const button = dropdownRef.current?.querySelector('button[data-headlessui-state="open"]') as HTMLElement;
    button?.click();
  });

  const handleLanguageSelect = (language: string) => {
    if (language === selectedLanguage) return;
  
    dispatch(setSelectedLanguage(language));
    localStorage.setItem("lang", language);
    document.cookie = `lang=${language}; path=/; max-age=31536000; SameSite=Lax`;
    i18n.changeLanguage(language);
  
    const segments = pathname.split("/").filter(Boolean);
  
    // ✅ Ensure language is updated without duplication
    if (segments[0] !== language) {
      segments[0] = language;
      router.replace(`/${segments.join("/")}` as Route);
    }
  };

  const handleCurrencySelect = (currency: string) => {
    if (currency === selectedCurrency) return;
    if (lockedCurrency && currency !== lockedCurrency) return;
  
    dispatch(setSelectedCurrency(currency));
    localStorage.setItem("currency", currency);
    document.cookie = `currency=${currency}; path=/; max-age=31536000; SameSite=Lax`;
  
    const segments = pathname.split("/").filter(Boolean);
  
    // ✅ Ensure currency is updated without duplication
    if (segments[1] !== currency) {
      segments[1] = currency;
      router.replace(`/${segments.join("/")}` as Route);
    }
  };

  const renderLang = () => (
    <div className="grid gap-8 lg:grid-cols-2">
      {languages.map((item) =>
        item.activated ? (
          <a
            key={item.code}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleLanguageSelect(item.code);
            }}
            className={`flex items-center p-2 -m-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none cursor-pointer ${
              selectedLanguage === item.code ? "bg-gray-200 dark:bg-gray-600" : ""
            }`}
          >
            <div>
              <p className="text-sm font-medium capitalize">{item.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{item.nativeName}</p>
            </div>
          </a>
        ) : null
      )}
    </div>
  );

  const renderCurr = () => (
    <div className="grid gap-8 lg:grid-cols-2">
      {(lockedCurrency ? currencies.filter((c) => c.code === lockedCurrency) : currencies).map((item) =>
        item.activated ? (
          <a
            key={item.code}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleCurrencySelect(item.code);
            }}
            className={`flex items-center p-2 -m-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none cursor-pointer ${
              selectedCurrency === item.code ? "bg-gray-200 dark:bg-gray-600" : ""
            }`}
          >
            <div>
              <p className="text-sm font-medium capitalize">{item.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{item.symbol}</p>
            </div>
          </a>
        ) : null
      )}
    </div>
  );

  return (
    <div ref={dropdownRef} className={`relative flex ${className}`}>
      <Popover style={{ display: "contents" }}>
        {({ open }) => (
          <>
            {/* Trigger inherits its color from the surrounding header
                (white on green in the redesign topbar, dark elsewhere). */}
            <Popover.Button className="group self-center h-10 sm:h-12 px-3 py-1.5 inline-flex items-center text-sm gap-1">
              <GlobeAltIcon className="w-5 h-5" />
              <span className="text-sm">{selectedLanguage?.split("_")[0].toUpperCase() || ""}</span>
              <span className="mx-1">/</span>
              <BanknotesIcon className="w-5 h-5" />
              <span className="text-sm">{selectedCurrency || ""}</span>
              <ChevronDownIcon className={`ml-1 h-4 w-4 transition ease-in-out duration-150 ${open ? "-rotate-180" : ""}`} />
            </Popover.Button>
            <Transition as={Fragment} enter="transition ease-out duration-200" enterFrom="opacity-0 translate-y-1" enterTo="opacity-100 translate-y-0">
              <Popover.Panel className={`absolute z-20 ${panelClassName}`}>
                {/* Pin dark text inside the panel so it doesn't inherit
                    white from the green-themed topbar above. */}
                <div className="p-3 rounded-2xl bg-white dark:bg-neutral-800 shadow-lg text-neutral-900 dark:text-neutral-100">
                  <Tab.Group>
                    <Tab.List className="flex space-x-1 rounded-full bg-gray-100 dark:bg-slate-700 p-1">
                      <Tab className="w-1/2 rounded-full py-2 text-sm font-medium text-gray-700">{t('language')}</Tab>
                      <Tab className="w-1/2 rounded-full py-2 text-sm font-medium text-gray-700">{t('currency')}</Tab>
                    </Tab.List>
                    <Tab.Panels className="mt-5">
                      <Tab.Panel className="rounded-xl p-3">{renderLang()}</Tab.Panel>
                      <Tab.Panel className="rounded-xl p-3">{renderCurr()}</Tab.Panel>
                    </Tab.Panels>
                  </Tab.Group>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default LangDropdown;
